import { Col, Flex, Row, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import SearchBox from 'components/SearchBox';
import RBCountryFilter from 'components/UniversalFilters/RBCountryFilter';
import SeasonIndexFilter from 'components/UniversalFilters/SeasonIndexFilter';
import HeaderCard from '../../components/HeaderCard';
import MultiSelect from '../../components/MultiSelect';
import Select from '../../components/Select';
import DashboardWidget from './components/dashboard-widget';
import RedirectedContentDrawer from './components/RedirectedContentDrawer';

import DashboardTable from 'screens/awd-dashboard/components/DashboardTable';
import DetailDrawer from 'screens/awd-dashboard/components/DetailDrawer';
import MobileDashboardTable from 'screens/awd-dashboard/components/MobileDashboardTable';
import TaskDrawer from 'screens/awd-dashboard/components/TaskDrawer';
import { SelectTaskData } from 'screens/sales-group-progress';
import { isMobileScreenSize } from 'utils/helpers';

import useAgronomistList from 'hooks/useAgronomistList';
import useCountryAndSeasonIndexFilter from 'hooks/useCountryAndSeasonIndexFilter';
import useDashboardQuery, { TDashboard } from 'hooks/useDashboardQuery';
import useSubDistrictList from 'hooks/useSubDistrictList';
import usePlantingGroupTableFilters from 'screens/awd-dashboard/hooks/usePlantingGroupTableFilters';

import { groupTypeOptions, SEASONS_STATUS } from '../../utils/constants';
import { constructDashboardQueryParams } from './util';

import './mobile.scss';
import './style.scss';

const Dashboard = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Country and season filters
  const {
    currentCountry: country,
    currentSeasonIdx: seasonIndex,
    setCurrentCountry: setCountry,
    setCurrentSeasonIdx: setSeasonIndex,
  } = useCountryAndSeasonIndexFilter();

  // Table filters
  const {
    agronomist,
    setAgronomist,
    subDistrict,
    setSubDistrict,
    groupType,
    setGroupType,
    seasonStatus,
    setSeasonStatus,
    currentPage,
    setCurrentPage,
    pageSize,
    plantingGroupName,
    setPlantingGroupName,
    plantingGroupSearchTerm,
    setPlantingGroupSearchTerm,
    onResetFilters: handleResetFilters,
    onPageChange: handlePageChange,
  } = usePlantingGroupTableFilters();

  // Table sorting
  const [sortedInfo, setSortedInfo] = useState<SorterResult<TDashboard>>({});
  const [sort, setSort] = useState('');

  // Selected data and drawer states
  const [selectedPlantingGroup, setSelectedPlantingGroup] =
    useState<TDashboard>();
  const [selectedTaskData, setSelectedTaskData] = useState<SelectTaskData>();
  const [isDetailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isTaskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [isRedirectDrawerOpen, setRedirectDrawerOpen] = useState(false);

  // URL parameters
  const seasonIdParam = searchParams.get('seasonId');
  const taskTypeParam = searchParams.get('taskType');

  // API queries
  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country);
  const { subDistrictOptions, isLoading: isSubDistrictLoading } =
    useSubDistrictList(country);

  // Dashboard query parameters
  const queryParams = useMemo(
    () =>
      constructDashboardQueryParams({
        pageSize,
        currentPage,
        country,
        seasonIndex,
        agronomist,
        subDistrict,
        seasonStatus,
        groupType,
        sort,
        plantingGroupName,
      }),
    [
      pageSize,
      currentPage,
      country,
      seasonIndex,
      agronomist,
      subDistrict,
      seasonStatus,
      groupType,
      sort,
      plantingGroupName,
    ],
  );

  const { data, isLoading } = useDashboardQuery(queryParams);

  // Event handlers
  const withClearFilters = useCallback(
    (callback: (...args: any[]) => void) => {
      return (...args: any[]) => {
        callback(...args);
        handleResetFilters();
        setPlantingGroupSearchTerm('');
        setSortedInfo({});
      };
    },
    [handleResetFilters, setPlantingGroupSearchTerm],
  );

  const handleCloseDrawer = useCallback(() => {
    setTaskDrawerOpen(false);
    setDetailDrawerOpen(false);
    setSelectedTaskData(undefined);
    setSelectedPlantingGroup(undefined);
    setRedirectDrawerOpen(false);
  }, []);

  const handleChange: TableProps<TDashboard>['onChange'] = (
    _,
    __,
    tableSort,
  ) => {
    const sortInfo = tableSort as SorterResult<TDashboard>;

    const sortString = sortInfo?.order
      ? `${sortInfo.columnKey},${sortInfo.order === 'ascend' ? 'asc' : 'desc'}`
      : '';

    setSort(sortString);
    setSortedInfo(sortInfo);
  };

  const handleSelectTask = useCallback((data: SelectTaskData) => {
    setSelectedTaskData(data);
    setTaskDrawerOpen(true);
  }, []);

  const handleSelectRow = useCallback((data: TDashboard) => {
    setSelectedPlantingGroup(data);
    setDetailDrawerOpen(true);
  }, []);

  const handleSearch = useCallback(
    (value: string) => {
      setCurrentPage(1);
      setPlantingGroupName(value);
    },
    [setCurrentPage, setPlantingGroupName],
  );

  const handleClearSearch = useCallback(() => {
    setCurrentPage(1);
    setPlantingGroupName('');
    setPlantingGroupSearchTerm('');
  }, [setCurrentPage, setPlantingGroupName, setPlantingGroupSearchTerm]);

  // Effects
  useEffect(() => {
    setRedirectDrawerOpen(!!(seasonIdParam && taskTypeParam));
  }, [seasonIdParam, taskTypeParam]);

  // Computed values
  const tableData = data?.data?.content || [];
  const totalElements = data?.data?.totalElements || 0;
  const selectedRowForTable =
    selectedPlantingGroup || (selectedTaskData as TDashboard);

  return (
    <div className="dashboard-container">
      <HeaderCard
        headerLabel={t('dashboard.awdProgress')}
        headerInfo={t('dashboard.description')}
        filters={
          <Flex gap={20} align="center" wrap="wrap">
            <RBCountryFilter
              selectedCountry={country}
              onChange={withClearFilters(setCountry)}
            />
            <SeasonIndexFilter
              country={country}
              seasonIndex={seasonIndex!}
              setSeasonIndex={withClearFilters(setSeasonIndex)}
            />
          </Flex>
        }
      />

      <Row className="mt-4">
        <Col span={24}>
          <Row>
            <Col span={24}>
              <DashboardWidget seasonId={seasonIndex ?? ''} />
            </Col>
          </Row>

          <Row>
            <Col span={24} className="table-container">
              <Row className="filters">
                <SearchBox
                  value={plantingGroupSearchTerm}
                  onChange={(e) => setPlantingGroupSearchTerm(e.target?.value)}
                  placeholder={t('dashboard.plantingGroupNamePlaceholder')}
                  onSearch={handleSearch}
                  onClear={handleClearSearch}
                  disabled={isLoading}
                  allowClear
                />

                <MultiSelect
                  label="Agronomist"
                  options={agronomistFilterOptions}
                  defaultValue={agronomist}
                  value={agronomist}
                  onChange={setAgronomist}
                  disabled={isLoading || isAgronomistLoading}
                  allowClear
                />

                <MultiSelect
                  label={t('genericForm.Sub-District')}
                  options={subDistrictOptions}
                  defaultValue={subDistrict}
                  value={subDistrict}
                  onChange={setSubDistrict}
                  disabled={isLoading || isSubDistrictLoading}
                  allowClear
                />

                <Select
                  label={t('farmerGroupList.seasonStatus')}
                  placeholder={t('genericForm.select', {
                    value: t('farmerGroupList.seasonStatus'),
                  })}
                  options={SEASONS_STATUS}
                  defaultValue={null}
                  value={seasonStatus}
                  onChange={setSeasonStatus}
                  disabled={isLoading}
                  allowClear
                />

                <Select
                  label={t('seasonProgressBar.groupType')}
                  placeholder={t('genericForm.select', {
                    value: t('seasonProgressBar.groupType'),
                  })}
                  options={groupTypeOptions}
                  value={groupType}
                  onChange={setGroupType}
                  disabled={isLoading}
                  allowClear
                />
              </Row>

              {isMobileScreenSize ? (
                <MobileDashboardTable
                  data={tableData}
                  loading={isLoading}
                  total={totalElements}
                  pageSize={pageSize}
                  current={currentPage}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                  onSelectTask={handleSelectTask}
                  onSelectRow={handleSelectRow}
                />
              ) : (
                <DashboardTable
                  data={tableData}
                  loading={isLoading}
                  total={totalElements}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  sortedInfo={sortedInfo}
                  selectedRow={selectedRowForTable}
                  handleChange={handleChange}
                  handlePageChange={handlePageChange}
                  onSelectTask={handleSelectTask}
                  onSelectRow={handleSelectRow}
                />
              )}

              <TaskDrawer
                data={selectedTaskData}
                open={isTaskDrawerOpen}
                onClose={handleCloseDrawer}
              />

              <DetailDrawer
                data={selectedPlantingGroup}
                open={isDetailDrawerOpen}
                onClose={handleCloseDrawer}
              />

              <RedirectedContentDrawer
                open={isRedirectDrawerOpen}
                onClose={handleCloseDrawer}
                seasonId={seasonIdParam ?? ''}
                taskType={taskTypeParam ?? ''}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
